import React, { useCallback, useEffect, useState } from 'react';
import { Mail, RefreshCw, Loader2, CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authFetch = (path, opts = {}) => {
  const token = localStorage.getItem('rbr_token');
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
};

const STATUS_TONE = {
  verified:  { label: 'Verified',  cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle2 },
  ok:        { label: 'Connected', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle2 },
  pending:   { label: 'Pending',   cls: 'text-amber-700 bg-amber-50 border-amber-200',       Icon: AlertTriangle },
  not_found: { label: 'Not set',   cls: 'text-stone-500 bg-stone-100 border-stone-200',      Icon: HelpCircle },
  failed:    { label: 'Failed',    cls: 'text-rose-700 bg-rose-50 border-rose-200',          Icon: XCircle },
  error:     { label: 'Error',     cls: 'text-rose-700 bg-rose-50 border-rose-200',          Icon: XCircle },
  missing:   { label: 'Missing',   cls: 'text-rose-700 bg-rose-50 border-rose-200',          Icon: XCircle },
  not_configured: { label: 'Not configured', cls: 'text-rose-700 bg-rose-50 border-rose-200', Icon: XCircle },
  unknown:   { label: 'Unknown',   cls: 'text-stone-500 bg-stone-100 border-stone-200',      Icon: HelpCircle },
};

const OVERALL = {
  healthy:  { label: 'Healthy',  cls: 'bg-emerald-600 text-white' },
  degraded: { label: 'Degraded', cls: 'bg-amber-500 text-white' },
  critical: { label: 'Critical', cls: 'bg-rose-600 text-white' },
};

const Pill = ({ status, testid }) => {
  const m = STATUS_TONE[status] || STATUS_TONE.unknown;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${m.cls}`}>
      <m.Icon className="w-3 h-3" /> {m.label}
    </span>
  );
};

const Stat = ({ label, children, testid }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-stone-100 last:border-0">
    <span className="text-[11px] text-stone-500">{label}</span>
    <span className="text-[11px] text-stone-800 text-right" data-testid={testid}>{children}</span>
  </div>
);

const DnsRecords = ({ records }) => {
  if (!records?.length) return null;
  return (
    <details className="mt-3" data-testid="email-health-dns-records">
      <summary className="text-[11px] font-semibold text-[#C79A4A] cursor-pointer">DNS records to publish ({records.length})</summary>
      <ul className="mt-2 space-y-2">
        {records.map((r, i) => (
          <li key={i} className="bg-white border border-stone-200 rounded-lg p-2 text-[10.5px]">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono font-semibold text-[#173F35]">{r.type} {r.name}</span>
              {r.priority != null && <span className="text-stone-500">prio {r.priority}</span>}
              <Pill status={r.status} />
            </div>
            <p className="font-mono break-all text-stone-600">{r.value}</p>
          </li>
        ))}
      </ul>
    </details>
  );
};

export const EmailHealthCard = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await authFetch('/api/admin/email-health/test-send', { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Test send failed');
      setTestResult(d);
      setHealth((h) => (h ? { ...h, recentLog: d.recentLog } : h));
      toast.success(`Test email accepted by Resend (id ${d.providerId})`);
    } catch (e) { toast.error(e.message || 'Test send failed'); setTestResult({ ok: false, error: e.message }); }
    finally { setTesting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch('/api/admin/email-health');
      if (r.ok) setHealth(await r.json());
      else setHealth({ overall: 'critical', provider: { status: 'error' }, message: 'Could not load email health.' });
    } catch { setHealth({ overall: 'critical', provider: { status: 'error' }, message: 'Could not load email health.' }); }
    finally { setLoading(false); }
  }, []);

  const recheck = async () => {
    setChecking(true);
    try {
      const r = await authFetch('/api/admin/email-health/recheck', { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Re-check failed');
      setHealth(d);
      if (d.overall === 'healthy') toast.success('Email domain verified — all users can receive mail.');
      else toast.warning(d.message || 'Domain still not verified.');
    } catch (e) { toast.error(e.message || 'Re-check failed'); }
    finally { setChecking(false); }
  };

  useEffect(() => { load(); }, [load]);

  const o = OVERALL[health?.overall] || { label: 'Checking…', cls: 'bg-stone-300 text-stone-700' };
  return (
    <div className="bg-[#FFFCF6] rounded-2xl border border-stone-200 p-4" data-testid="email-health-card">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-4 h-4 text-[#C79A4A]" />
        <h4 className="text-sm font-semibold text-stone-800">Email health</h4>
        <span data-testid="email-health-overall" className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${o.cls}`}>{o.label}</span>
        <button
          onClick={recheck}
          disabled={checking || loading}
          data-testid="email-health-recheck"
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white border border-stone-200 hover:border-[#C79A4A] text-[11px] text-stone-700 disabled:opacity-60"
        >
          {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Re-check DNS
        </button>
      </div>
      {loading && !health ? (
        <p className="text-[12px] text-stone-500 py-4 text-center flex items-center justify-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking email provider…</p>
      ) : health && (
        <>
          <p className="text-[12px] text-stone-600 mb-2" data-testid="email-health-message">{health.message}</p>
          <Stat label="Sending domain" testid="email-health-domain"><span className="font-mono">{health.domain || '—'}</span></Stat>
          <Stat label="Domain verification"><Pill status={health.domainStatus} testid="email-health-domain-status" /></Stat>
          <Stat label="SPF"><Pill status={health.spf} testid="email-health-spf" /></Stat>
          <Stat label="DKIM"><Pill status={health.dkim} testid="email-health-dkim" /></Stat>
          <Stat label="DMARC"><Pill status={health.dmarc} testid="email-health-dmarc" /></Stat>
          <Stat label="Email provider"><Pill status={health.provider?.status} testid="email-health-provider" /></Stat>
          <Stat label="Last DNS check" testid="email-health-checked-at">{health.checkedAt ? new Date(health.checkedAt).toLocaleString() : '—'}</Stat>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              onClick={sendTest}
              disabled={testing}
              data-testid="email-health-test-send"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#173F35] text-[#FFFCF6] text-[11px] font-semibold hover:bg-[#245549] disabled:opacity-60"
            >
              {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Send test email to me
            </button>
            {testResult?.ok && (
              <span className="text-[10.5px] text-stone-600 font-mono" data-testid="email-health-test-result">
                id {testResult.providerId} · {testResult.lastEvent || 'queued'} · from {testResult.sender}
              </span>
            )}
          </div>
          <DnsRecords records={health.records} />
        </>
      )}
    </div>
  );
};

export default EmailHealthCard;
